"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Typography,
} from "@mui/material";
import {
    Search as SearchIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";

import {
    setOptions,
    importLibrary,
} from "@googlemaps/js-api-loader";

import { GC_MAP_KEY } from "../../utils/config/config";
import { removeChars } from "../../utils/helper/appHelpers";
import {
    fetchAddressFromLatLng,
} from "../../utils/helper/locationHelper";
import Scrollbar from "../comman/Scrollbar";

let placesLibraryPromise;

const loadPlacesLibrary = () => {
    if (!placesLibraryPromise) {
        setOptions({
            key: GC_MAP_KEY,
            v: "weekly",
        });

        placesLibraryPromise = importLibrary("places");
    }

    return placesLibraryPromise;
};

export default function PlaceSearch({
    placeholder,
    defaultValue,
    onselect = () => {},
    type = "address",
    error,
    helperText,
    disabled,
}) {
    const [searchText, setSearchText] = useState(defaultValue || "");
    const [showPredictions, setShowPredictions] = useState(false);
    const [placePredictions, setPlacePredictions] = useState([]);
    const [placesLibrary, setPlacesLibrary] = useState(null);

    useEffect(() => {
        setSearchText(defaultValue || "");
    }, [defaultValue]);

    useEffect(() => {
        let cancelled = false;

        loadPlacesLibrary()
            .then((places) => {
                if (!cancelled) {
                    setPlacesLibrary(places);
                }
            })
            .catch((err) => {
                console.error("Google Places initialization failed:", err);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const handleValueChange = async (e) => {
        const input = removeChars(e.target.value);

        setSearchText(input);

        if (!input.trim() || !placesLibrary) {
            setPlacePredictions([]);
            setShowPredictions(false);
            return;
        }

        setShowPredictions(true);

        try {
            const { suggestions = [] } =
                await placesLibrary.AutocompleteSuggestion
                    .fetchAutocompleteSuggestions({
                        input,
                    });

            setPlacePredictions(
                suggestions
                    .filter((suggestion) => suggestion.placePrediction)
                    .map((suggestion) => suggestion.placePrediction)
            );
        } catch (err) {
            console.error("Google Places autocomplete failed:", err);
            setPlacePredictions([]);
        }
    };

    const getFilteredPredictions = useMemo(() => {
        return placePredictions.filter((prediction) => {
            const types = prediction.types || [];

            if (type === "country") {
                return types.includes("country");
            }

            if (type === "state") {
                return types.includes("administrative_area_level_1");
            }

            if (type === "city") {
                return (
                    types.includes("locality") ||
                    types.includes("sublocality") ||
                    types.includes("postal_town")
                );
            }

            return true;
        });
    }, [placePredictions, type]);

    const handleSelect = async (item) => {
        setShowPredictions(false);

        const description = item.text?.text || "";
        const mainText = item.mainText?.text || description;

        if (type === "country" || type === "state" || type === "city") {
            setSearchText(mainText);
            onselect(mainText);
            setPlacePredictions([]);
            return;
        }

        setSearchText(description);
        setPlacePredictions([]);

        try {
            const place = item.toPlace();

            await place.fetchFields({
                fields: ["location", "addressComponents"],
            });

            const location = place.location;

            if (!location) {
                console.error("Selected place has no location.");
                return;
            }

            const lat = location.lat();
            const lng = location.lng();

            const fetchAddress = await fetchAddressFromLatLng(lat, lng);

            onselect({
                lat,
                lng,
                ...fetchAddress,
            });
        } catch (err) {
            console.error("Failed to retrieve selected place:", err);
        }
    };

    const handleClear = () => {
        setSearchText("");
        setPlacePredictions([]);
        setShowPredictions(false);

        if (type === "country" || type === "state" || type === "city") {
            onselect("");
        } else {
            onselect({
                lat: 0,
                lng: 0,
                address: "",
                city: "",
                state: "",
                country: "",
                area: "",
                pincode: "",
            });
        }
    };

    return (
        <Box sx={{ position: "relative" }}>
            <TextField
                size="small"
                variant="outlined"
                fullWidth
                value={searchText}
                onChange={handleValueChange}
                placeholder={placeholder || "Search for an address"}
                disabled={disabled}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <IconButton size="small" disabled>
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            {searchText && !disabled && (
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    edge="end"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
                error={error}
                helperText={helperText}
            />

            {showPredictions && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "background.paper",
                        boxShadow: "0 .5rem 1rem rgba(0, 0, 0, .15)",
                        zIndex: 200000,
                        borderRadius: 1,
                    }}
                >
                    <Scrollbar sx={{ maxHeight: 300 }}>
                        <List>
                            {getFilteredPredictions.length > 0 ? (
                                getFilteredPredictions.map((item, i) => (
                                    <ListItemButton
                                        key={`place_${i}`}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <Typography variant="cardHeading">
                                            {item.text?.text}
                                        </Typography>
                                    </ListItemButton>
                                ))
                            ) : (
                                <ListItemButton disabled>
                                    <Typography variant="cardHeading">
                                        No data found
                                    </Typography>
                                </ListItemButton>
                            )}
                        </List>
                    </Scrollbar>
                </Box>
            )}
        </Box>
    );
}
